import { createSlice } from "@reduxjs/toolkit";

import { softReset } from "shared/reducers/classroom";
import {
  KeyboardShortcutSignalType,
  LocalModalContentType,
  LocalSliceStateType,
  PubsubMessageType,
  SidebarPanelType,
} from "shared/types";
import { PreclassTestResultType } from "shared/types/preclass_check";

const initialState: LocalSliceStateType = {
  chatTextAreaContent: "",
  chatTextAreaContentOverride: "",
  currentUserId: "",
  currentlyBackingUpPoll: null,
  flightMonitoringUrl: null,
  hasNewChatMessages: false,
  hasStageChanged: false,
  keyboardShortcutSignal: KeyboardShortcutSignalType.None,
  localModalContent: LocalModalContentType.None,
  overlayError: null,
  pubsubMessage: null,
  pubsubErrorCode: null,
  showInstructorNotes: false,
  sidebarPanel: SidebarPanelType.None,
  preclassCheckResults: {},
  joinTimestamp: null,
};

const localSlice = createSlice({
  name: "local",
  initialState,
  reducers: {
    clearPubsubMessage(state) {
      state.pubsubMessage = null;
    },

    setChatTextAreaContent(state, action) {
      const { text } = action.payload;
      state.chatTextAreaContent = text;
    },

    setChatTextAreaContentOverride(state, action) {
      const { text } = action.payload;
      state.chatTextAreaContentOverride = text;
    },

    setCurrentUserId(state, action) {
      const { userId } = action.payload;
      state.currentUserId = userId;
    },

    setCurrentlyBackingUpPoll(state, action) {
      const { pollId } = action.payload;
      state.currentlyBackingUpPoll = pollId;
    },

    setFlightMonitoringUrl(state, action) {
      const { flightMonitoringUrl } = action.payload;
      state.flightMonitoringUrl = flightMonitoringUrl;
    },

    setHasNewChatMessages(state, action) {
      state.hasNewChatMessages = action.payload;
    },

    setHasStageChanged(state, action) {
      const { hasStageChanged } = action.payload;
      state.hasStageChanged = hasStageChanged;
    },

    setKeyboardShortcutSignal(state, action) {
      const { signal } = action.payload;
      state.keyboardShortcutSignal = signal;
    },

    setPubsubErrorCode(state, action) {
      const { code } = action.payload;
      state.pubsubErrorCode = code;
    },

    setPubsubMessage(state, action) {
      state.pubsubMessage = action.payload as PubsubMessageType;
    },

    showLocalModal(state, action) {
      const { content } = action.payload;
      state.localModalContent = content;
    },

    hideLocalModal(state) {
      state.localModalContent = LocalModalContentType.None;
    },

    setShowInstructorNotes(state, action) {
      const { showInstructorNotes } = action.payload;
      state.showInstructorNotes = showInstructorNotes;
    },

    setSidebarPanel(state, action) {
      const { panel } = action.payload;
      state.sidebarPanel = panel;
    },

    toggleSidebarPanel(state, action) {
      const { panel } = action.payload;

      if (state.sidebarPanel === panel) {
        state.sidebarPanel = SidebarPanelType.None;
      } else {
        state.sidebarPanel = panel;
      }
    },

    addPreclassCheckResult(state, action) {
      const result = action.payload as PreclassTestResultType;
      const previousResult = state.preclassCheckResults[result.name];
      // deep compare results and only update if it changes
      if (previousResult) {
        if (
          previousResult.outcome === result.outcome &&
          previousResult.error === result.error &&
          previousResult.category === result.category
        ) {
          return;
        }
      }
      state.preclassCheckResults[result.name] = result;
    },

    setJoinTimestamp(state, action) {
      const { timestamp } = action.payload;
      state.joinTimestamp = timestamp;
    },

    setOverlayError(state, action) {
      const { error } = action.payload;
      state.overlayError = error;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(softReset, (state) => {
      state.hasStageChanged = false;
    });
  },
});

export const {
  clearPubsubMessage,
  setChatTextAreaContent,
  setChatTextAreaContentOverride,
  setCurrentUserId,
  setCurrentlyBackingUpPoll,
  setFlightMonitoringUrl,
  setHasNewChatMessages,
  setHasStageChanged,
  setKeyboardShortcutSignal,
  setOverlayError,
  setPubsubErrorCode,
  setPubsubMessage,
  showLocalModal,
  hideLocalModal,
  setShowInstructorNotes,
  setSidebarPanel,
  toggleSidebarPanel,
  addPreclassCheckResult,
  setJoinTimestamp,
} = localSlice.actions;

export const localSliceReducer = localSlice.reducer;
